import { styled } from '@abyss/web/tools/styled';
import { Accordion } from '@abyss/web/ui/Accordion';
import { Button } from '@abyss/web/ui/Button';
import { CheckboxGroup } from '@abyss/web/ui/CheckboxGroup';
import { Divider } from '@abyss/web/ui/Divider';
import { Drawer } from '@abyss/web/ui/Drawer';
import { Heading } from '@abyss/web/ui/Heading';
import { Markdown } from '@abyss/web/ui/Markdown';
import { Text } from '@abyss/web/ui/Text';
import { ToggleSwitch } from '@abyss/web/ui/ToggleSwitch';

export const StyledDrawer = styled(Drawer, {
  zIndex: '1080 !important',
  '.abyss-modal-header-container': {
    width: 'fit-content',
  },
  '.abyss-modal-content-container': {
    borderRadius: 0,
    '@screen < $xl': {
      '& .abyss-modal-body > div': {
        marginLeft: '0px',
        marginRight: '0px',
      },
    },
    '@screen < $sm': {
      top: '25px',
      height: 'calc(100vh - 26px)',
      width: '100%',
      borderRadius: '20px 20px 0px 0px',
      color: '$primary1',
      border: '1px solid $primary1',
      '& .abyss-modal-body > div': {
        marginLeft: '16px',
        marginRight: '16px',
      },
    },
  },
  '.abyss-modal-close-button': {
    zIndex: '9999 !important',
    '@screen < $sm': {
      top: '12px',
      right: '12px',
    },
  },
  '.abyss-modal-body': {
    scrollbarWidth: 'none' /* Hide scrollbar for Chrome, Safari and Opera */,
    '::-webkit-scrollbar': {
      display: 'none',
    },
    overflow: 'auto !important',
  },
});

const headingStyle = {
  fontFamily: '$primary !important',
  fontWeight: '700 !important',
  fontSize: '23px !important',
  lineHeight: '24px !important',
  letterSpacing: '0px',
};

export const HeadingText = styled(Heading, {
  ...headingStyle,
});

export const HeadingTextOnBoarding = styled('span', {
  fontFamily: '$primary',
  fontWeight: '700 ',
  fontSize: '18px',
  lineHeight: '24px',
  letterSpacing: '0px',
  color: '$gray8',
});

export const DescriptionTextOnboarding = styled(Text, {
  fontSize: '16px',
  fontWeight: '400',
  lineHeight: '22px',
  color: '$gray7',
  marginTop: '2px',
});

export const OnboardingDisclaimerText = styled('span', {
  fontSize: '14px',
  fontWeight: '500',
  lineHeight: '18px',
  color: '#323333',
  letterSpacing: '0px',
  display: 'block',
});

export const FlexContainer = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
  marginTop: '16px',
  marginBottom: '16px',
  gap: '12px',
});

export const ListItemIconContainer = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  height: '24px',
});

export const ListItemContentContainer = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
});

export const ToggleSwitchField = styled(ToggleSwitch, {
  padding: '4px 16px 4px 16px',
  '.abyss-toggle-switch-label': { marginLeft: '8px' },
});

export const AccordionContainer = styled(Accordion, {
  paddingLeft: '16px',
  paddingRight: '16px',
  fontFamily: '$primary',
  backgroundColor: 'transparent',
  '& .abyss-accordion-trigger': {
    color: '$gray8',
    padding: '0px',
    cursor: 'pointer',
    border: 'none',
    '&:hover': { backgroundColor: '$gray1' },
    '& .abyss-accordion-header': {
      fontWeight: '500',
      fontSize: '18px',
    },
    '& .abyss-accordion-header-subtext': {
      fontSize: '16px',
      fontWeight: '400',
      color: '$gray8',
    },
    '& .abyss-heading-root': {
      fontSize: '18px',
      fontWeight: '500',
    },
  },
  '& .abyss-radio-group-container': { marginTop: '16px' },
  '& .abyss-checkbox-group-container': {
    marginTop: '16px',
    maxHeight: '300px',
    overflow: 'auto',
  },
  '& .abyss-checkbox-flex-container': {
    margin: '4px',
  },
  '& .abyss-accordion-content-text': { padding: '0px 0px 10px 4px' },
});

export const InputLikeAccordionContainer = styled(Accordion, {
  fontFamily: '$primary',
  backgroundColor: 'transparent',

  '& .abyss-accordion-trigger': {
    color: '$gray8',
    cursor: 'pointer',
    border: '1px solid #D1D3D4',
    borderRadius: '4px',
    backgroundColor: '#FFFFFF',
    position: 'relative',
    height: '40px',
    minHeight: '40px',
    maxHeight: '40px',
    boxSizing: 'border-box',
    padding: '0 12px',
    '&:hover': {
      backgroundColor: '#F7F8F8',
      borderColor: '#898A8C',
    },
    '&:focus': {
      outline: 'none',
      border: '2px solid #004BA0',
      borderRadius: '4px',
    },
    '&:focus-visible': {
      outline: 'none',
      border: '2px solid $interactive1',
      boxShadow: '0px 0px 0px 2px $interactive1',
    },
    '& .abyss-accordion-header': {
      fontWeight: '400',
      fontSize: '16px',
      paddingRight: '40px',
    },
    '& .abyss-accordion-header-subtext': {
      fontSize: '16px',
      fontWeight: '400',
      color: '#4B4D4F',
    },
    '& .abyss-heading-root': {
      display: 'none',
    },
  },
  '& .abyss-checkbox-group-container': {
    marginTop: '16px',
    maxHeight: '300px',
    overflow: 'auto',
  },
  '& .abyss-checkbox-flex-container': {
    margin: '4px',
  },
  '& .abyss-accordion-content-text': { padding: '0px ' },
});

export const ClearButtonWrapper = styled('div', {
  position: 'absolute',
  right: '50px',
  top: '50%',
  transform: 'translateY(-50%)',
  display: 'flex',
  alignItems: 'center',
  zIndex: 1,
});

export const StyledMarkdown = styled(Markdown, {
  fontSize: '18px',
  fontWeight: '400',
  lineHeight: '24px',
  color: '$gray8',
  'abyss-layout-stack': {
    paddingTop: '8px',
  },
  '.abyss-icon-symbol': {
    fontSize: '18px !important',
    paddingTop: '2px',
    color: '$interactive1 !important',
  },
  '.abyss-link-root': {
    fontSize: '14px',
    lineHeight: '18px',
    borderColor: 'white !important',
  },
  '.abyss-flex-root': {
    paddingLeft: '5px',
  },
  '.abyss-accordion-trigger': {
    border: 'none',
    padding: '0px',
  },
});

export const ClearAllButton = styled(Button, {
  background: 'transparent !important',
  border: 'none !important',
  padding: '0',
  cursor: 'pointer',
  outline: 'none',
  boxShadow: 'none',
  '&:hover': {
    background: 'transparent !important',
    border: 'none ',
    boxShadow: 'none',
  },
});

export const StyledDivider = styled(Divider, {
  '&.abyss-divider-root': {
    border: '0.5px solid #CCCCCC',
    marginTop: '$md !important',
    marginBottom: '$md !important',
  },
});

export const StyledCheckBoxGroup = styled(CheckboxGroup, {
  '&.abyss-checkbox-group-container': {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    flexDirection: 'column',
    gap: '16px',
  },
});

export const InfoStyledDrawer = styled(Drawer, {
  '.abyss-modal-header-container': {
    borderBottom: '2px solid #E5E5E6 !important',
  },
});

export const NewStyledDrawer = styled(Drawer, {
  zIndex: '1080 !important',
  '.abyss-modal-header-container': {
    borderBottom: '2px solid #E5E5E6 !important',
  },
  '.abyss-modal-content-title': {
    paddingTop: '0px !important',
  },
  '.abyss-modal-content-container': {
    borderRadius: 0,
    '@screen < $xl': {
      '& .abyss-modal-body > div': {
        marginLeft: '0px',
        marginRight: '0px',
      },
    },
    '@screen < $sm': {
      color: '$primary1',
      border: '1px solid $primary1',
      '& .abyss-modal-body > div': {
        marginLeft: '16px',
        marginRight: '16px',
      },
    },
  },
  '.abyss-modal-close-button': {
    zIndex: '9999 !important',
    top: '12px',
    right: '12px',
  },
  '.abyss-modal-body': {
    scrollbarWidth: 'none' /* Hide scrollbar for Chrome, Safari and Opera */,
    '::-webkit-scrollbar': {
      display: 'none',
      overflow: 'auto !important',
    },
  },
  '.abyss-modal-footer': {
    zIndex: '1080 !important',
    padding: '16px',
    borderTop: '2px solid #E5E5E6 !important',
  },
});

export const SearchInputWrapper = styled('div', {
  padding: '7px 14px 10px 14px',
  borderBottom: '1px solid #D1D3D4',
});
